import app from 'flarum/forum/app';
import extractText from 'flarum/common/utils/extractText';
import api from './api';
import * as tones from './tones';
import deviceAlert from './deviceAlert';
const t = (key, params) => app.translator.trans('ernestdefoe-parley-calls.forum.' + key, params);
const ENDED = ['completed', 'unanswered', 'declined', 'failed'];

/*
 * The call engine (WebRTC, signalling, the session's phases) is a chunk of its
 * own: guests and members who cannot call never download it. Fetched as soon
 * as Calls boots for someone who can, so a ring finds it already loaded.
 */
let Session = null;
let loading = null;
const loadSession = () => loading ??= import(/* webpackChunkName: 'forum/Session', webpackMode: 'lazy-once' */ './Session').then(mod => Session = mod.default);

/**
 * The one call this tab is on, or being rung for. Reached as `app.parleyCalls`.
 */
export default class Calls {
  constructor() {
    this.session = null;
    this.config = app.forum.attribute('parleyCalls') || {};
    this.me = Number(app.session.user.id());
  }
  available() {
    return !!(this.config.canCall && this.config.ready && window.RTCPeerConnection && navigator.mediaDevices);
  }

  /**
   * Open the mic and speaker check.
   *
   * Public so the alerts, the settings page and anything a theme adds can all
   * reach the same dialog rather than each importing the component.
   */
  async checkDevices() {
    /*
     * Lazy, like Session and VoiceSession above it. Most visitors never open
     * this dialog, and it rides on forum.js — which every visitor downloads on
     * every page — so it belongs in a chunk. The loader is already paid for by
     * those two, which is what makes the split worth it here: 6.3 KB raw /
     * 1.7 KB gzip off the every-page bundle for no new overhead.
     */
    const {
      default: DeviceCheck
    } = await import(/* webpackChunkName: 'forum/components/DeviceCheck', webpackMode: 'lazy-once' */ './components/DeviceCheck');
    app.modal.show(DeviceCheck);
  }
  canShare() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) && !/Android|iPhone|iPad/i.test(navigator.userAgent);
  }
  boot() {
    loadSession();

    // The ringing screen's name is set in Bricolage. Fetch it now, so the
    // first ring is drawn in it rather than in the fallback while it loads.
    document.fonts?.load('700 24px "Parley Bricolage"').catch(() => {});
    window.addEventListener('pagehide', () => {
      const s = this.session;
      if (s && s.call.id && s.phase !== 'ended' && s.phase !== 'incoming') api.beaconEnd(s.call.id);
    });
    window.addEventListener('beforeunload', e => {
      if (this.session && ['live', 'connecting', 'outgoing'].includes(this.session.phase)) {
        e.preventDefault();
        e.returnValue = '';
      }
    });

    // With no live socket, rings and answers would never arrive. Ask instead,
    // every few seconds, for as long as the socket is down.
    setInterval(() => {
      if (app.parley.socketUp() || document.visibilityState !== 'visible') return;
      api.current().then(r => this.polled(r && r.call));
    }, 3000);

    // A reload during a call cannot pick the call back up; end it cleanly.
    // A ring that is still going is shown, so a reload does not lose it.
    api.current().then(r => {
      const call = r && r.call;
      if (!call) return;
      if (call.status === 'ringing' && call.calleeId === this.me) this.ringing(call);else api.end(call.id);
    });
  }
  bind(channel) {
    channel.bind('parley.call.ring', d => this.ringing(d.call));
    channel.bind('parley.call.state', d => this.changed(d.call));
    channel.bind('parley.call.signal', d => {
      if (this.session && this.session.call.id === d.callId) this.session.receive(d);
    });
  }

  /** What a poll found, handled the way the matching push would be. */
  polled(call) {
    const s = this.session;
    if (call) {
      if (!s && call.status === 'ringing' && call.calleeId === this.me) return this.ringing(call);
      if (s && s.call.id === call.id && s.call.status !== call.status) {
        s.call.status = call.status;
        return this.changed(call);
      }
      return;
    }
    // The call we were on is no longer current: it ended while we were not
    // listening. The history row says how.
    if (s && s.call.id && s.phase !== 'incoming') this.changed({
      ...s.call,
      status: 'completed'
    });else if (s && s.phase === 'incoming') this.drop();
  }

  // ── Placing a call ────────────────────────────────────────────────────────

  async start(conversationId, kind, peer) {
    if (!Session) await loadSession();
    if (this.session) {
      app.alerts.show({
        type: 'error'
      }, t('already_on_call'));
      return;
    }

    // A call takes you out of any room's voice chat (the server does too).
    app.parleyVoice?.leave();
    const s = this.session = new Session({
      id: null,
      kind,
      callee: peer,
      caller: null,
      ringSeconds: this.config.ringSeconds
    }, 'caller');
    m.redraw();
    try {
      await s.openMedia();
    } catch (e) {
      this.drop();
      deviceAlert(kind === 'video' ? 'no_camera' : 'no_microphone');
      return;
    }
    try {
      s.call = await api.start(conversationId, kind).then(r => r.call);
    } catch (e) {
      this.drop();
      return; // The refusal ("X is busy") is shown by Flarum's error alert.
    }
    if (this.session !== s) return;
    tones.ringback();
    s.ringTimer = setTimeout(() => s.phase === 'outgoing' && this.hangUp(), (s.call.ringSeconds || 45) * 1000);
    m.redraw();
  }

  // ── Being called ──────────────────────────────────────────────────────────

  ringing(call) {
    if (!Session) return void loadSession().then(() => this.ringing(call));
    if (this.session) return;
    const s = this.session = new Session(call, 'callee');
    s.onFail = () => this.failed();
    tones.ring();
    s.ringTimer = setTimeout(() => this.session === s && s.phase === 'incoming' && this.drop(), (call.ringSeconds || 45) * 1000 + 5000);
    m.redraw();
  }
  async accept() {
    const s = this.session;
    if (!s || s.phase !== 'incoming') return;
    tones.stop();
    s.phase = 'connecting';
    m.redraw();
    try {
      await s.openMedia();
    } catch (e) {
      api.decline(s.call.id);
      this.drop();
      deviceAlert(s.kind === 'video' ? 'no_camera' : 'no_microphone');
      return;
    }
    s.owner = true;
    await api.accept(s.call.id);
    s.accepted = true;
    s.startCatchUp();
    // The caller's offer may already be waiting.
    if (s.queued.some(q => q.type === 'offer')) await s.connect();
    m.redraw();
  }
  decline() {
    const s = this.session;
    if (!s) return;
    api.decline(s.call.id);
    this.drop();
  }

  // ── Ending ────────────────────────────────────────────────────────────────

  hangUp() {
    const s = this.session;
    if (!s) return;
    if (s.call.id) api.end(s.call.id);
    this.drop();
  }
  failed() {
    const s = this.session;
    if (!s) return;
    if (s.call.id) api.fail(s.call.id);
    this.drop();
    app.alerts.show({
      type: 'error'
    }, t('could_not_connect'));
  }
  drop() {
    this.session?.close();
    this.session = null;
    m.redraw();
  }

  /** The server says the call moved on: answered, declined, rung out, ended. */
  changed(call) {
    const s = this.session;
    if (!s || s.call.id && s.call.id !== call.id) return;
    if (!s.call.id) return; // Our own start() has not returned yet.

    s.call.status = call.status;
    if (call.status === 'active') {
      if (s.pc) return; // Already connecting; a poll repeating the news.
      clearTimeout(s.ringTimer);
      s.call = {
        ...s.call,
        ...call
      };
      if (s.role === 'caller') {
        s.onFail = () => this.failed();
        s.connect().catch(() => this.failed());
      } else if (!s.owner) {
        // Answered in another of this person's tabs.
        this.drop();
      }
    } else if (ENDED.includes(call.status)) {
      const iCalled = s.role === 'caller';
      const name = extractText(s.peer?.displayName || '');
      this.drop();
      if (iCalled && call.status === 'declined') app.alerts.show({
        type: 'error'
      }, t('declined', {
        name
      }));
      if (iCalled && call.status === 'unanswered') app.alerts.show({
        type: 'error'
      }, t('no_answer', {
        name
      }));
    }
    m.redraw();
  }
}flarum.reg.addChunkModule('ernestdefoe-parley-calls:forum/Session', '721', 'ernestdefoe-parley-calls', 'forum/Session');
flarum.reg.addChunkModule('ernestdefoe-parley-calls:forum/components/DeviceCheck', '919', 'ernestdefoe-parley-calls', 'forum/components/DeviceCheck');
flarum.reg.addChunkModule('ernestdefoe-parley-calls:forum/components/DeviceCheck', '919', 'ernestdefoe-parley-calls', 'forum/devices');